import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isBoardOfficerRole, normalizeRoleKeyForPermissions } from '../utils/iamRoles';

function resolveEffectiveFrontendRole(roleRaw, isPortalScope) {
  const normalized = normalizeRoleKeyForPermissions(roleRaw);
  // Règle produit: hors `/portal`, les rôles bureau utilisent l'expérience participant.
  // Sous `/portal`, on applique le rôle réel (executive / finance_lead / operations_lead).
  if (!isPortalScope && isBoardOfficerRole(normalized)) return 'participant';
  return normalized;
}

// Définition des permissions par rôle (identique au backend — clés génériques)
const ROLE_PERMISSIONS = {
  "admin": [
    // Membres
    "members.view", "members.view_own", "members.create", "members.edit", "members.delete", "members.deactivate", "members.bulk_operations",
    // Contributions
    "contributions.view", "contributions.view_own", "contributions.create", "contributions.create_own", "contributions.edit", "contributions.delete", "contributions.validate", "contributions.approve", "contributions.export", "contributions.bulk_create",
    // Finances
    "financial.view", "financial.view_own", "financial.create", "financial.edit", "financial.delete", "financial.approve", "financial.export",
    // Réunions
    "meetings.view", "meetings.view_public", "meetings.create", "meetings.edit", "meetings.delete", "meetings.publish",
    // Documents
    "documents.view", "documents.view_public", "documents.view_own", "documents.create", "documents.edit", "documents.delete", "documents.download",
    // Événements
    "events.view", "events.view_public", "events.create", "events.edit", "events.delete",
    // Utilisateurs
    "users.view", "users.view_own", "users.create", "users.edit", "users.edit_own", "users.delete",
    // Rôles et permissions
    "roles.view", "roles.manage",
    // Tableau de bord
    "dashboard.view", "dashboard.stats", "dashboard.stats_financial", "dashboard.stats_advanced",
    // Paramètres
    "settings.view", "settings.edit",
    // People (fiches profils)
    "people.profiles.read", "people.profiles.create", "people.profiles.update", "people.profiles.deactivate", "people.profiles.reactivate", "people.profiles.submit_review", "people.stats.read",
  ],
  
  "executive": [
    // Membres
    "members.view", "members.create", "members.edit", "members.deactivate",
    // Contributions
    "contributions.view", "contributions.create", "contributions.edit", "contributions.delete", "contributions.validate", "contributions.approve", "contributions.export", "contributions.bulk_create",
    // Finances
    "financial.view", "financial.create", "financial.edit", "financial.delete", "financial.approve",
    // Réunions
    "meetings.view", "meetings.create", "meetings.edit", "meetings.delete", "meetings.publish",
    // Documents
    "documents.view", "documents.create", "documents.edit", "documents.delete", "documents.download",
    // Événements
    "events.view", "events.create", "events.edit", "events.delete",
    // Tableau de bord
    "dashboard.view", "dashboard.stats", "dashboard.stats_financial",
    // Paramètres
    "settings.view",
    "people.profiles.read", "people.profiles.create", "people.profiles.update", "people.profiles.deactivate", "people.profiles.reactivate", "people.profiles.submit_review", "people.stats.read",
  ],
  
  "finance_lead": [
    // Membres
    "members.view",
    // Contributions
    "contributions.view", "contributions.create", "contributions.edit", "contributions.delete", "contributions.validate", "contributions.approve", "contributions.export", "contributions.bulk_create",
    // Finances
    "financial.view", "financial.create", "financial.edit", "financial.delete", "financial.export",
    // Documents
    "documents.view", "documents.download",
    // Événements
    "events.view",
    // Tableau de bord
    "dashboard.view", "dashboard.stats_financial",
    "people.profiles.read", "people.stats.read",
  ],
  
  "operations_lead": [
    // Membres
    "members.view", "members.create", "members.edit", "members.deactivate",
    // Contributions
    "contributions.view",
    // Réunions
    "meetings.view", "meetings.create", "meetings.edit", "meetings.publish",
    // Documents
    "documents.view", "documents.create", "documents.edit", "documents.delete", "documents.download",
    // Événements
    "events.view", "events.create", "events.edit", "events.delete",
    // Tableau de bord
    "dashboard.view",
    "people.profiles.read", "people.profiles.create", "people.profiles.update", "people.profiles.deactivate", "people.profiles.reactivate", "people.profiles.submit_review", "people.stats.read",
  ],
  
  "participant": [
    // Membres
    "members.view_own",
    // Contributions
    "contributions.view_own", "contributions.create_own",
    // Finances
    "financial.view_own",
    // Réunions
    "meetings.view_public",
    // Documents
    "documents.view_public", "documents.download",
    // Événements
    "events.view_public",
    // Utilisateurs
    "users.view_own", "users.edit_own",
    // Tableau de bord
    "dashboard.view",
    "people.profiles.read",
    "people.profiles.submit_review",
  ],
};

// Alias IAM canoniques (alignés plateforme — voir uruganda_core.iam.roles)
ROLE_PERMISSIONS.org_admin = ROLE_PERMISSIONS.admin;
ROLE_PERMISSIONS.organization_admin = ROLE_PERMISSIONS.admin;
ROLE_PERMISSIONS.saas_admin = ROLE_PERMISSIONS.admin;
ROLE_PERMISSIONS.org_manager = ROLE_PERMISSIONS.operations_lead;
ROLE_PERMISSIONS.admin_project = ROLE_PERMISSIONS.admin;

/**
 * Hook pour vérifier les permissions de l'utilisateur actuel
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isPortalScope = String(location?.pathname || '').startsWith('/portal');

  const positionRoleKeys = useMemo(() => {
    const raw = user?.position_role_keys;
    if (!Array.isArray(raw)) return [];
    return raw.map((x) => String(x || '').trim()).filter(Boolean);
  }, [user?.position_role_keys]);

  const permissions = useMemo(() => {
    if (!user || user.role == null || user.role === '') {
      return [];
    }

    const role = resolveEffectiveFrontendRole(user.role, isPortalScope);
    const merged = new Set(ROLE_PERMISSIONS[role] || []);
    for (const pk of positionRoleKeys) {
      const rk = resolveEffectiveFrontendRole(pk, isPortalScope);
      for (const p of ROLE_PERMISSIONS[rk] || []) merged.add(p);
    }
    return [...merged];
  }, [user, isPortalScope, positionRoleKeys]);
  
  /**
   * Vérifie si l'utilisateur a une permission donnée
   */
  const hasPermission = (permission) => {
    if (!user || user.role == null || user.role === '') {
      return false;
    }

    const raw = String(user.role).toLowerCase();

    // Plateforme + administrateur organisation (toutes les permissions)
    if (
      raw === 'admin' ||
      raw === 'org_admin' ||
      raw === 'organization_admin' ||
      raw === 'admin_project' ||
      raw === 'saas_admin'
    ) {
      return true;
    }

    return permissions.includes(permission);
  };
  
  /**
   * Vérifie si l'utilisateur a au moins une des permissions données
   */
  const hasAnyPermission = (permissionList) => {
    return permissionList.some(permission => hasPermission(permission));
  };
  
  /**
   * Vérifie si l'utilisateur a toutes les permissions données
   */
  const hasAllPermissions = (permissionList) => {
    return permissionList.every(permission => hasPermission(permission));
  };
  
  /**
   * Vérifie si l'utilisateur peut accéder à un module donné
   */
  const canAccessModule = (module) => {
    // Vérifier s'il y a au moins une permission pour ce module
    const modulePermissions = permissions.filter(p => p.startsWith(`${module}.`));
    return modulePermissions.length > 0;
  };
  
  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessModule,
    role:
      user?.role != null && user.role !== ''
        ? resolveEffectiveFrontendRole(user.role, isPortalScope)
        : null,
  };
};
